import Cookies from "js-cookie";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";

const shouldPrintTrackingInfo = (): boolean =>
  Cookies.get(`digipost.feature.printTrackingInfo`) === "true";

export enum SiteImproveActions {
  TO_SIGNERING = "Bruker gikk til signeringsportalen",
  PARTNER_ACCESS = "Bruker ønsker partner-tilgang",
  API_ACCESS = "Bruker ønsker API-tilgang",
  TO_POSTKASSE = "Bruker gikk til digital postkasse",
  BANKID2 = "Bruker klikket på autentiseringsmetode bankid2",
  BUYPASS = "Bruker klikket på autentiseringsmetode buypass",
  FROM_SIGNERING = "en bruker kom inn fra signering for å registrere virksomhet",
  POST = "post",
  ORGANISATION_DONE_TO_DIGIPOST = "en bruker har registrert en bedrift og blir sendt til digipost",
  UNDERKONTO_SENDT = "forespørsel om opprettelse av underkonto er sendt til admingodkjenning",
  UNDERKONTO_OFFENTLIG = "en underkonto har blitt opprettet (offentlig)",
  CONTINUE = "bruker klikker på fortsett",
  NEW_ACCOUNT = "bruker klikker lag digipostkonto",
  REOPEN = "bruker klikker på jeg gjenapne",
  CANCEL = "bruker klikker på avbryt",
  LOGOUT = "bruker klikker på logout",
  WRITE_EPOST_INPUT = "bruker skriver i epostfeltet",
  WRITE_COUNTRY_CODE_INPUT = "bruker skriver i country code-feltet",
  WRITE_PHONE_INPUT = "bruker skriver i mobilnummerfeltet",
}

export enum SiteImproveCategories {
  GENERAL = "registrering",
  SIGNERING_VIRKSOMHET = "registrering-virksomhet-signering",
  REDIRECT = "redirect",
  ORGANISATION_DONE = "registrering-bedrift-done",
  UNDERKONTO = "registrering-underkonto",
  PERSON_REGISTRATION = "person-registrering",
}

type SiteImprovePageView = [
  "trackdynamic",
  {
    url: string;
    ref?: string;
    title: string;
  },
];

type SiteImproveToggleTrackingAction = ["notrack"] | ["notrack", false];

export type SiteImproveAction =
  | SiteImprovePageView
  | SiteImproveEvent
  | SiteImproveToggleTrackingAction;

const siteImproveCache: Array<SiteImproveAction> = [];

const siteImproveScriptTagIsPresent = (): boolean =>
  document.getElementById("stag") !== null;

type SiteImproveEventWithLabel = {
  action: SiteImproveActions;
  category: SiteImproveCategories;
  label?: string;
};

type SiteImproveEvent = [
  "event",
  SiteImproveCategories,
  SiteImproveActions,
  string | undefined,
];

export function siteImproveEvent({
  category,
  action,
  label,
}: SiteImproveEventWithLabel): void {
  if (shouldPrintTrackingInfo()) {
    console.log(`Event:
      category: ${category}
      action: ${action}
      `);
  }
  const event: SiteImproveAction = ["event", category, action, label];
  if (window._sz && siteImproveScriptTagIsPresent()) {
    window._sz.push(event);
  } else {
    siteImproveCache.push(event);
  }
}

export function siteImprovePageview(url: string, ref?: string): void {
  const urlPath = window.redactSiteImproveUrl(url);
  const fullUrl = window.location.origin + urlPath;

  let fullRefUrl: string | undefined;
  if (ref !== undefined) {
    const pageviewPath = window.redactSiteImproveUrl(ref);
    fullRefUrl = window.location.origin + pageviewPath;
  }

  if (shouldPrintTrackingInfo()) {
    console.log(`PageView:
      url: ${fullUrl}
      ref: ${fullRefUrl}
      `);
  }

  if (window._sz) {
    window._sz.push([
      "trackdynamic",
      {
        url: fullUrl,
        ref: fullRefUrl,
        title: "Digipost.no",
      },
    ]);
  }
}

export const usePageviewTracker = (): void => {
  const ref = useRef<boolean>(false);
  const location = useLocation();
  const [previousTrackedPage, setPreviousTrackedPage] = useState<string>();

  useEffect(() => {
    const newPage = location.pathname + location.hash;

    if (ref.current) {
      const referrerPath =
        document.referrer === ""
          ? undefined
          : new URL(document.referrer).pathname;

      const pageRef = previousTrackedPage ?? referrerPath;

      if (newPage !== pageRef) {
        siteImprovePageview(newPage, pageRef);
        setPreviousTrackedPage(newPage);
      }
    } else {
      setPreviousTrackedPage(newPage);
      ref.current = true;
    }
  }, [location.pathname, location.hash, previousTrackedPage]);
};

export const enableSiteImproveTracking = (): void => {
  const enableEvent: SiteImproveToggleTrackingAction = ["notrack", false];

  if (siteImproveScriptTagIsPresent()) {
    if (!window._sz) {
      siteImproveCache.push(enableEvent);
    } else {
      window._sz.push(enableEvent);
    }
    return;
  }

  const script = document.createElement("script");
  script.src = "https://siteimproveanalytics.com/js/siteanalyze_6015663.js";
  script.id = "stag";
  script.async = true;
  script.onload = () => {
    setTimeout(() => {
      siteImproveCache.forEach((action) => {
        if (window._sz) {
          window._sz.push(action);
        }
      });
    }, 500);
  };

  document.head.appendChild(script);
};
